import { useCallback, useEffect, useRef, useState } from 'react'
import type { Badge, BadgeColor, VaultBadgesApi } from '../LibraryDeps'

/** The palette, in swatch order. Keys only — library.css maps each to a readable chip.
 *  Keep in sync with the backend (vault_badges.Color) and the migration's CHECK. */
export const BADGE_COLORS: readonly BadgeColor[] = ['red', 'orange', 'amber', 'green', 'teal', 'blue', 'indigo', 'purple', 'pink', 'grey']
/** Matches the backend caps (vault_badges.MAX_NAME_LEN / MAX_BADGES / MAX_IDS). */
export const BADGE_NAME_MAX = 24
export const BADGES_MAX = 50
export const BADGE_ITEMS_PER_CALL = 500

/** Grid tiles show this many chips, then "+N"; phone tiles this many dots. */
export const TILE_CHIPS = 2
export const TILE_DOTS = 3

/** A colour for a new badge: the first one the Account isn't using yet. */
export function nextColor(badges: Badge[]): BadgeColor {
  const used = new Set(badges.map((b) => b.color))
  return BADGE_COLORS.find((c) => !used.has(c)) ?? BADGE_COLORS[badges.length % BADGE_COLORS.length]!
}

/** The item's badges in catalog (creation) order; ids no longer in the catalog are dropped. */
export function badgesFor(ids: number[] | undefined, catalog: Badge[]): Badge[] {
  if (!ids?.length) return []
  const on = new Set(ids)
  return catalog.filter((b) => on.has(b.id))
}

/**
 * The Account's Badge set: loaded once, kept in creation order, changed in place.
 * Absent `api` = badges unavailable (nothing is shown).
 */
export function useBadgeCatalog(api: VaultBadgesApi | undefined) {
  const [badges, setBadges] = useState<Badge[]>([])
  const current = useRef(api)

  useEffect(() => {
    current.current = api
    setBadges([])
    if (!api) return
    api
      .list()
      .then((list) => current.current === api && setBadges(list))
      // Not loaded (e.g. before the migration lands): no badges, nothing breaks.
      .catch(() => undefined)
  }, [api])

  const put = useCallback((b: Badge) => setBadges((list) => (list.some((x) => x.id === b.id) ? list.map((x) => (x.id === b.id ? b : x)) : [...list, b])), [])

  const create = useCallback(
    async (name: string, color: BadgeColor) => {
      if (!api) throw new Error('Badges unavailable')
      const b = await api.create(name, color)
      put(b)
      return b
    },
    [api, put],
  )
  const update = useCallback(
    async (id: number, patch: { name?: string; color?: BadgeColor }) => {
      if (!api) return
      put(await api.update(id, patch))
    },
    [api, put],
  )
  const remove = useCallback(
    async (id: number) => {
      if (!api) return
      await api.remove(id)
      setBadges((list) => list.filter((b) => b.id !== id))
    },
    [api],
  )
  /** Attach/detach on many items, chunked to the backend's per-call cap. */
  const setItems = useCallback(
    async (id: number, add: string[], removeIds: string[]) => {
      if (!api) return
      let last: Badge | null = null
      for (let i = 0; i < add.length; i += BADGE_ITEMS_PER_CALL) last = await api.setItems(id, add.slice(i, i + BADGE_ITEMS_PER_CALL), [])
      for (let i = 0; i < removeIds.length; i += BADGE_ITEMS_PER_CALL) last = await api.setItems(id, [], removeIds.slice(i, i + BADGE_ITEMS_PER_CALL))
      if (last) put(last)
    },
    [api, put],
  )

  return { badges, available: Boolean(api), create, update, remove, setItems }
}

export type BadgeCatalog = ReturnType<typeof useBadgeCatalog>
